/**
 * GridDataService – mirrors Angular grid-data.service.ts.
 * Singleton service (plain module) that manages timeline configuration,
 * column definitions, and cell-level logic for the ag-grid workspace.
 */
import type { ColDef, ColGroupDef, ColSpanParams, CellClassParams } from 'ag-grid-community';
import type { TimelineMap, TimelineQuarter, TimelinePeriod } from '../models';
import type { Department } from '../models/organization';
import { httpGet } from './httpService';
import { environment } from '../environments/environment';
import {
  TIMELINE_API,
  Quarter as Q, Period as P, Week as W,
  TIME_QUARTER, TIME_PERIOD, TIME_WEEK,
} from '../constants';
import {
  isGroundRow,
  isCellEditable as cellEditable,
  isCellHasEndingSeasonValue,
  isExcitementKpiRow,
  isQuarterRow,
  isPeriodRow,
  isWeekRow,
} from '../helpers/gridUtilities';
import { MultiSeasonTooltip } from '../components/MultiSeasonTooltip';

// ── Module-level state (acts like an Angular @Injectable singleton) ──

let timelineConfigData: unknown = null;
const timelineMap: TimelineMap = { quarters: new Map() };
let gridRowDataKeys: string[] = [];
const weekToCellFieldNameMap = new Map<string, string>();
const weekToSeasonMap = new Map<string, string[]>();
const cellFieldNameToWeekMap = new Map<string, string>();
let selectedDepartmentInfo: Department | null = null;

// ── Public helpers ────────────────────────────────────────────────

export function getGridRowDataKeys(): string[] { return gridRowDataKeys; }
export function getWeekToCellFieldNameMap(): Map<string, string> { return weekToCellFieldNameMap; }
export function getWeekToSeasonMap(): Map<string, string[]> { return weekToSeasonMap; }
export function getCellFieldNameToWeekMap(): Map<string, string> { return cellFieldNameToWeekMap; }
export function getTimelineMap(): TimelineMap { return timelineMap; }

export function setSelectedDepartmentInfo(dept: Department | null): void {
  selectedDepartmentInfo = dept;
}

// ── Timeline fetching with 7 AM UTC cache ─────────────────────────

export async function fetchTimeConfig(): Promise<void> {
  const cachedData = localStorage.getItem('timelineConfigData');
  const cachedTimestamp = localStorage.getItem('timelineConfigTimestamp');
  const nowUTC = new Date();

  if (cachedData && cachedTimestamp) {
    const lastUpdatedUTC = new Date(cachedTimestamp);
    const nextRefreshDate = new Date(lastUpdatedUTC);
    nextRefreshDate.setUTCDate(lastUpdatedUTC.getUTCDate() + 1);
    nextRefreshDate.setUTCHours(7, 0, 0, 0);
    if (nowUTC < nextRefreshDate) {
      timelineConfigData = JSON.parse(cachedData);
      return;
    }
  }

  const url = environment.apiConfig.articleplan_api.uri + TIMELINE_API;
  const data = await httpGet<unknown>(url);
  timelineConfigData = data;
  localStorage.setItem('timelineConfigData', JSON.stringify(data));
  localStorage.setItem('timelineConfigTimestamp', nowUTC.toISOString());
}

// ── Timeline map creation ─────────────────────────────────────────

export function ensureTimelineMap(): void {
  if (timelineMap.quarters.size > 0 || !timelineConfigData) return;
  createTimelineMap(timelineConfigData as Record<string, unknown>);
}

function createTimelineMap(config: Record<string, unknown>): void {
  timelineMap.currentWeek = (config as { currentWeekNumberWithYear?: string }).currentWeekNumberWithYear;
  gridRowDataKeys = [];
  weekToCellFieldNameMap.clear();
  cellFieldNameToWeekMap.clear();
  weekToSeasonMap.clear();

  const quarters = (config as { quarters: Record<string, { quarterName: string; periods: Array<{ periodName: string; weeks: Array<{ weekWithYear: string; seasons: string[] }> }> }> }).quarters;
  let quarterCount = 1;
  for (const qKey in quarters) {
    const qInfo = quarters[qKey];
    const quarterModel: TimelineQuarter = { name: qInfo.quarterName, periods: new Map() };
    timelineMap.quarters.set(Q + quarterCount, quarterModel);

    let periodCount = 1;
    for (const pInfo of qInfo.periods) {
      const periodModel: TimelinePeriod = { name: pInfo.periodName, weeks: new Map() };
      quarterModel.periods.set(P + periodCount, periodModel);

      let weekCount = 1;
      for (const wInfo of pInfo.weeks) {
        const weekStr = wInfo.weekWithYear.toString();
        periodModel.weeks.set(W + weekCount, weekStr);
        const fieldKey = `${Q}${quarterCount}_${P}${periodCount}_${W}${weekCount}`;
        gridRowDataKeys.push(fieldKey);
        weekToCellFieldNameMap.set(weekStr, fieldKey);
        cellFieldNameToWeekMap.set(fieldKey, weekStr);
        weekToSeasonMap.set(weekStr, wInfo.seasons);
        weekCount++;
      }
      periodCount++;
    }
    quarterCount++;
  }
}

// ── Column definitions ────────────────────────────────────────────

export function getColumnDefinitionsForTimeline(
  readonlyPlan: boolean,
  baseColDefs: (ColDef | ColGroupDef)[],
  isIncludeExcitementToggleChecked = false,
  onTooltipChange?: (data: { field: string; isLatestSeason: boolean; kpi: string; value: number; isMainMarket: boolean }) => void,
): (ColDef | ColGroupDef)[] {
  ensureTimelineMap();
  const colDefs = [...baseColDefs];

  for (const key of gridRowDataKeys) {
    const [q, p, w] = key.split('_');
    const quarter = timelineMap.quarters.get(q);
    if (!quarter) continue;

    const headerCol = findOrCreateQuarterGroup(colDefs, quarter);
    const period = quarter.periods.get(p);
    const isLastPeriodInQuarter = p === `P${quarter.periods.size}`;
    const periodCol = findOrCreatePeriodGroup(headerCol, period, isLastPeriodInQuarter);

    const week = period?.weeks.get(w);
    const weekDisplay = week?.slice(-2);
    const existingWeekCol = (periodCol as ColGroupDef).children?.find((c) => c.headerName === weekDisplay);
    if (!existingWeekCol) {
      const weekCol = buildWeekColDef({
        fieldKey: key, week, weekDisplay, weekKey: w, period, quarter,
        isLastPeriodInQuarter, readonlyPlan, isIncludeExcitementToggleChecked, onTooltipChange,
      });
      (periodCol as ColGroupDef).children?.push(weekCol);
    }
  }

  return colDefs;
}

// ── Column group helpers ──────────────────────────────────────────

function findOrCreateQuarterGroup(
  colDefs: (ColDef | ColGroupDef)[],
  quarter: TimelineQuarter,
): ColDef | ColGroupDef {
  let headerCol = colDefs.find((c) => c.headerName === quarter.name);
  if (!headerCol) {
    headerCol = { headerName: quarter.name, headerClass: 'quarter-header-border-right', children: [] };
    colDefs.push(headerCol);
  }
  return headerCol;
}

function findOrCreatePeriodGroup(
  headerCol: ColDef | ColGroupDef,
  period: TimelinePeriod | undefined,
  isLastPeriodInQuarter: boolean,
): ColDef | ColGroupDef {
  let periodCol = (headerCol as ColGroupDef).children?.find((c) => c.headerName === period?.name);
  if (!periodCol) {
    periodCol = {
      headerName: period?.name,
      headerClass: isLastPeriodInQuarter ? 'quarter-header-border-right' : 'period-header-border-right',
      children: [],
    };
    (headerCol as ColGroupDef).children.push(periodCol);
  }
  return periodCol;
}

// ── Week column definition builder ────────────────────────────────

interface WeekColDefOptions {
  fieldKey: string;
  week: string | undefined;
  weekDisplay: string | undefined;
  weekKey: string;
  period: TimelinePeriod | undefined;
  quarter: TimelineQuarter;
  isLastPeriodInQuarter: boolean;
  readonlyPlan: boolean;
  isIncludeExcitementToggleChecked: boolean;
  onTooltipChange?: (data: { field: string; isLatestSeason: boolean; kpi: string; value: number; isMainMarket: boolean }) => void;
}

function buildWeekHeaderClasses(
  week: string | undefined,
  weekKey: string,
  period: TimelinePeriod | undefined,
  isLastPeriodInQuarter: boolean,
): string[] {
  const classes: string[] = [];
  const seasons = weekToSeasonMap.get(week ?? '');
  if (seasons && seasons.length >= 2) {
    classes.push('overlap-week-header');
  }
  if (weekKey === `W${period?.weeks.size}`) {
    classes.push(isLastPeriodInQuarter ? 'quarter-header-border-right' : 'period-header-border-right');
  }
  return classes;
}

function buildWeekColDef(options: WeekColDefOptions): ColDef {
  const {
    fieldKey, week, weekDisplay, weekKey, period, quarter,
    isLastPeriodInQuarter, readonlyPlan, isIncludeExcitementToggleChecked, onTooltipChange,
  } = options;
  const weekHeaderClasses = buildWeekHeaderClasses(week, weekKey, period, isLastPeriodInQuarter);
  const isOverlapWeek = weekToSeasonMap.get(week ?? '')?.length === 2;

  return {
    headerName: weekDisplay,
    headerTooltip: week,
    headerClass: weekHeaderClasses,
    field: fieldKey,
    cellStyle: (params: { data?: Record<string, unknown> }) => getColSpanCellStyle(params),
    editable: (params: { data?: Record<string, unknown> }) =>
      !readonlyPlan && cellEditable(params, week, timelineMap.currentWeek ?? '', weekToSeasonMap) &&
      !isCellHasEndingSeasonValue({ data: params.data, colDef: { field: fieldKey } }) &&
      isEditingAllowedForOldSeason(week),
    resizable: false,
    sortable: false,
    cellEditor: 'agNumberCellEditor',
    valueParser: (params: { newValue: unknown }) => {
      const parsed = Number(params.newValue);
      return Number.isNaN(parsed) ? 0 : Math.min(999, Math.max(0, parsed));
    },
    valueFormatter: (params: { value?: unknown }) => {
      if (params.value == null) return '';
      if (typeof params.value === 'number' || typeof params.value === 'string') return String(params.value);
      return '';
    },
    cellEditorParams: { min: 0, max: 999, preventStepping: true },
    suppressMovable: true,
    colSpan: (params: ColSpanParams) => calculateColSpan(params, quarter),
    cellClass: (params: CellClassParams) =>
      buildCellClasses(params, fieldKey, readonlyPlan, week, isIncludeExcitementToggleChecked),
    tooltipComponent: isOverlapWeek ? MultiSeasonTooltip : undefined,
    tooltipValueGetter: isOverlapWeek ? (params: { value?: unknown }) => params.value ?? 0 : undefined,
    tooltipComponentParams: () => ({
      isGridEditable: !readonlyPlan,
      cellFieldNameToWeekMap,
      weekToSeasonMap,
      currentWeek: timelineMap.currentWeek,
      isLatestSeason: isEditingAllowedForLatestSeason(week),
      isOldSeason: isEditingAllowedForOldSeason(week),
      onTooltipChange,
    }),
  } as ColDef;
}

// ── Cell class builder (returns string[] for ag-grid) ─────────────

function applyExcitementClass(
  classes: string[],
  params: CellClassParams,
  isIncludeExcitementToggleChecked: boolean,
): void {
  if (isExcitementKpiRow(params) && !isIncludeExcitementToggleChecked) {
    classes.push('excitement-kpi-cell');
  }
}

function buildCellClasses(
  params: CellClassParams,
  fieldName: string,
  readonlyPlan: boolean,
  week: string | undefined,
  isIncludeExcitementToggleChecked: boolean,
): string[] {
  if (!params.data) return [];

  const classes: string[] = [];
  const timeLevel = params.data.timeLevel as string;
  const isCalculated = params.data.isCalculated as boolean;
  const kpiName = params.data.kpi as string;
  const isGround = isGroundRow(params.data.planLevel as string | undefined);
  const hasEndingSeason = isCellHasEndingSeasonValue({ data: params.data, colDef: { field: fieldName } });

  // ── 1. Time-level border classes ──────────────────────────────
  getClassesByTimeLevel(timeLevel, classes, fieldName);

  // ── 2. "Sum Fin Q" top border ─────────────────────────────────
  if (kpiName === 'Sum Fin Q') {
    classes.push('thick-border-top');
  }

  // ── 3. Ending-season value highlight (highest priority bg) ────
  //    Skip for calculated/summary rows – they should keep kpi-cell-style instead
  if (hasEndingSeason && !isCalculated) {
    classes.push('cell-with-es-value');
    applyExcitementClass(classes, params, isIncludeExcitementToggleChecked);
    return classes;
  }

  // ── 4. Ground rows ────────────────────────────────────────────
  if (isGround) {
    classes.push('past-week-cell');
    applyExcitementClass(classes, params, isIncludeExcitementToggleChecked);
    return classes;
  }

  // ── 5. Excitement KPI rows ────────────────────────────────────
  applyExcitementClass(classes, params, isIncludeExcitementToggleChecked);

  // ── 6. Calculated rows (Sum Fin Q, Period sum, Article index, Week sum) ──
  //    Applied for ALL time levels (quarter, period, week)
  if (isCalculated) {
    classes.push('kpi-cell-style');
    return classes;
  }

  // ── 7. Editable vs past-week for non-calculated week rows ─────
  if (timeLevel === TIME_WEEK) {
    const isEditable = cellEditable(params as { data: Record<string, unknown> }, week, timelineMap.currentWeek ?? '', weekToSeasonMap);
    const isSeasonAllowed = isEditingAllowedForOldSeason(week);
    if (readonlyPlan || !isEditable || !isSeasonAllowed) {
      classes.push('past-week-cell');
    }
  }

  return classes;
}

function getClassesByTimeLevel(timeLevel: string, classes: string[], fieldName: string) {
  if (timeLevel === TIME_QUARTER) {
    classes.push('quarter-border-right');
  } else if (timeLevel === TIME_PERIOD) {
    classes.push(isLastPeriodOfQuarter(fieldName) ? 'quarter-border-right' : 'period-border-right');
  } else if (timeLevel === TIME_WEEK) {
    if (isQuarterEndBoundary(fieldName)) classes.push('quarter-border-right');
    else if (isPeriodEndBoundary(fieldName)) classes.push('period-border-right');
  }
}

// ── Inline styles ONLY for colspan rows (quarter/period borders) ──

function getColSpanCellStyle(
  params: { data?: Record<string, unknown> },
): Record<string, string> {
  const styles: Record<string, string> = { textAlign: 'center' };
  if (!params.data) return styles;

  const timeLevel = params.data.timeLevel as string;

  // Only apply inline border styles for colspan rows where CSS classes
  // won't work because ag-grid merges cells
  if (timeLevel === TIME_QUARTER) {
    styles.borderRight = '2px solid var(--bads-color-primary-30, #c8c8c8)';
  } else if (timeLevel === TIME_PERIOD) {
    styles.borderRight = '1.8px solid var(--bads-color-primary-30, #c8c8c8)';
  }

  return styles;
}

// ── Season editing helpers ────────────────────────────────────────

function isEditingAllowedForOldSeason(week: string | undefined): boolean {
  const seasons = weekToSeasonMap.get(week ?? '');
  if (!seasons || seasons.length === 0) return true;
  if (selectedDepartmentInfo?.recommendedEndSeasonNumber == null) return true;
  // For overlap weeks: check old (first) season; for non-overlap: check the only season
  return Number(seasons[0]) <= Number(selectedDepartmentInfo.recommendedEndSeasonNumber);
}

function isEditingAllowedForLatestSeason(week: string | undefined): boolean {
  const seasons = weekToSeasonMap.get(week ?? '');
  const latestSeasonNumber = seasons?.length === 2 ? seasons[1] : undefined;
  return (
    selectedDepartmentInfo?.recommendedEndSeasonNumber == null ||
    (latestSeasonNumber !== undefined && Number(latestSeasonNumber) <= Number(selectedDepartmentInfo.recommendedEndSeasonNumber))
  );
}

// ── ColSpan ───────────────────────────────────────────────────────

function calculateColSpan(params: ColSpanParams, quarter: TimelineQuarter): number {
  if (isQuarterRow(params)) {
    // Sum total weeks across all periods in this quarter
    let totalWeeks = 0;
    quarter.periods.forEach((p) => { totalWeeks += p.weeks.size; });
    return totalWeeks;
  }
  if (isPeriodRow(params)) {
    // Find which period this cell belongs to and span its actual week count
    const field = params.colDef?.field ?? '';
    const [, pKey] = field.split('_');
    const period = quarter.periods.get(pKey);
    return period?.weeks.size ?? 4;
  }
  if (isWeekRow(params)) return 1;
  // Default: span entire quarter
  let totalWeeks = 0;
  quarter.periods.forEach((p) => { totalWeeks += p.weeks.size; });
  return totalWeeks;
}

// ── Border boundary helpers ───────────────────────────────────────

function isQuarterEndBoundary(field: string): boolean {
  const [quarter, period, week] = field.split('_');
  const qMap = timelineMap.quarters.get(quarter);
  if (!qMap) return false;
  if (period !== `P${qMap.periods.size}`) return false;
  const pMap = qMap.periods.get(period);
  return week === `W${pMap?.weeks.size}`;
}

function isPeriodEndBoundary(field: string): boolean {
  const [quarter, period, week] = field.split('_');
  const qMap = timelineMap.quarters.get(quarter);
  if (!qMap) return false;
  const pMap = qMap.periods.get(period);
  if (!pMap || week !== `W${pMap.weeks.size}`) return false;
  return period !== `P${qMap.periods.size}`;
}

function isLastPeriodOfQuarter(field: string): boolean {
  const [quarter, period] = field.split('_');
  const qMap = timelineMap.quarters.get(quarter);
  return period === `P${qMap?.periods.size}`;
}

// ── Reset (useful when switching departments) ─────────────────────

export function resetTimelineState(): void {
  timelineMap.quarters.clear();
  timelineMap.currentWeek = undefined;
  gridRowDataKeys = [];
  weekToCellFieldNameMap.clear();
  cellFieldNameToWeekMap.clear();
  weekToSeasonMap.clear();
  timelineConfigData = null;
}
